import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X, ChevronDown, ChevronUp, GripVertical } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Modal, Input, Select, message, Pagination } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import backIcon from '../../../../../Images/addstarsbackIcon.png';
import Editicon from "../../../../../Images/editstages.png";
import { Get_Form_Details, Add_Update_Form, Get_Question_Types, Get_Answer_Types, Get_Only_Question_In_Form, Add_Update_Question_Admin_Panel, Get_All_Form_Question_And_Options, Delete_Complete_Form, Delete_Question_Option_In_Form } from '../../../../../services/userAPI';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import PageBreadcrumb from '../../../../../reusableField/PageBreadcrumb';
import BackIcon from '../../../../../Images/backIcon.png';
import HomeIcon from '../../../../../Images/homeIcon.png';

const FormsConfiguration = () => {
    const [forms, setForms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState('list');
    const [selectedForm, setSelectedForm] = useState(null);
    const [editingForm, setEditingForm] = useState({
        name: '',
        description: '',
        fields: []
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [formToDelete, setFormToDelete] = useState(null);
    const [showDeleteQuestionModal, setShowDeleteQuestionModal] = useState(false);
    const [questionToDelete, setQuestionToDelete] = useState(null);
    const [deleteQuestionLoading, setDeleteQuestionLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [isFormModified, setIsFormModified] = useState(false);
    const [isQuestionsModified, setIsQuestionsModified] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [formErrors, setFormErrors] = useState({});
    const [fieldErrors, setFieldErrors] = useState({});
    const [questionErrors, setQuestionErrors] = useState({});
    const [editQuestionErrors, setEditQuestionErrors] = useState({});
    const [currentFormId, setCurrentFormId] = useState(null);
    const [currentFormName, setCurrentFormName] = useState('');
    const [questions, setQuestions] = useState([]);
    const [editingQuestion, setEditingQuestion] = useState({
        fieldName: '',
        questionType: null,
        answerType: null,
        starQuestionRequired: null,
        hintText: '',
        maxLines: '',
        reValidation: '',
        reValidationText: '',
        contributingToFormDate: null,
        options: [],
        questionId: null
    });
    const [duplicateCheckLevel, setDuplicateCheckLevel] = useState('USER');
    const [duplicateCheckQuestions, setDuplicateCheckQuestions] = useState([]);
    const [showDuplicateCheck, setShowDuplicateCheck] = useState(false);
    const [expandedQuestions, setExpandedQuestions] = useState({});
    const [questionsFromEdit, setQuestionsFromEdit] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(5);
    const [cursor, setCursor] = useState(null);
    const [pageMeta, setPageMeta] = useState(null);

    const location = useLocation();
    const navigate = useNavigate();
    const fullState = location.state || {};

    const dartsFormNeeded = location.state?.dartsFormNeeded;
    const accountId = location.state?.accountId;
    const roleId = location.state?.roleId;
    const pocName = location.state?.pocName;
    const pocEmail = location.state?.pocEmail;
    const pocNumber = location.state?.pocNumber;
    const numberLogin = location.state?.numberLogin;
    const emailLogin = location.state?.emailLogin;
    const mandatoryTimeDistribution = location.state?.mandatoryTimeDistribution;
    const targetEditDays = location.state?.targetEditDays;
    const inputEditDays = location.state?.inputEditDays;
    const name = location.state?.name;
    const dartsNeeded = location.state?.dartsNeeded;
    const clanNeeded = location.state?.clanNeeded;
    const syncNeeded = location.state?.syncNeeded;
    const accountName = location.state?.accountName;

    const handleBack = () => {
        navigate("/admin");
    };

    const handleDiscardClick = () => {
        navigate("/admin");
    };

    const [fetchFormDetails, { loading: apiLoading, error, data: formDetailsData, refetch }] = useLazyQuery(Get_Form_Details, {
        fetchPolicy: 'network-only',
    });

    // Fetch form details with pagination
    useEffect(() => {
        if (roleId) {
            fetchFormDetails({
                variables: {
                    roleId: parseInt(roleId),
                    pagingCriteria: {
                        limit: pageSize,
                        cursor: cursor,
                        sortByField: "rank",
                        sortOrder: "desc"
                    }
                }
            });
        }
    }, [roleId, cursor, pageSize]);

    const [fetchQuestionTypes, { data: questionTypesData }] = useLazyQuery(Get_Question_Types, {
        fetchPolicy: 'network-only',
    });

    const [fetchAnswerTypes, { data: answerTypesData }] = useLazyQuery(Get_Answer_Types, {
        fetchPolicy: 'network-only',
    });

    const questionTypes = questionTypesData?.getQuestionTypes?.map(item => ({
        value: item.toString(),
        label: item.toString()
    })) || [];

    const answerTypes = answerTypesData?.getAnswerTypes?.map(item => ({
        value: item.toString(),
        label: item.toString()
    })) || [];

    const [fetchFormQuestions, { data: formQuestionsData, loading: formQuestionsLoading }] = useLazyQuery(Get_Only_Question_In_Form, {
        fetchPolicy: 'network-only',
    });

    const formQuestionsList = formQuestionsData?.getOnlyQuestionInForm || [];

    const [fetchAllFormQuestions, { data: allFormQuestionsData, loading: allFormQuestionsLoading }] = useLazyQuery(Get_All_Form_Question_And_Options, {
        fetchPolicy: 'network-only',
    });

    const [addUpdateForm, { loading: mutationLoading }] = useMutation(Add_Update_Form, {
        onCompleted: (data) => {
            if (data?.addUpdateForm?.success) {
                refetch();
                const formId = data.addUpdateForm.data.id;
                setCurrentFormId(formId);
                setCurrentFormName(data.addUpdateForm.data.formName);
                setFormErrors({});
                setFieldErrors({});
                setQuestions([]);
                setEditingQuestion({
                    fieldName: '',
                    questionType: null,
                    answerType: null,
                    starQuestionRequired: null,
                    hintText: '',
                    maxLines: '',
                    reValidation: '',
                    reValidationText: '',
                    contributingToFormDate: null,
                    options: [],
                    questionId: null
                });
                // Set questionsFromEdit based on current view before changing it
                setQuestionsFromEdit(view === 'edit');
                // Go to questions view after saving form
                setView('questions');
                setShowDuplicateCheck(false);
            } else {
                message.error(data?.addUpdateForm?.message || 'Failed to save form.');
            }
        },
        onError: (error) => {
            console.error('Error saving form:', error);
        }
    });

    const [addUpdateQuestion, { loading: questionMutationLoading }] = useMutation(Add_Update_Question_Admin_Panel, {
        onCompleted: (data) => {
            if (data?.addUpdateQuestionAdminPanel?.success) {
                console.log('Question saved successfully:', data.addUpdateQuestionAdminPanel.data);
            }
        },
        onError: (error) => {
            console.error('Error saving question:', error);
        }
    });

    const [deleteCompleteForm] = useMutation(Delete_Complete_Form, {
        onCompleted: (data) => {
            if (data?.deleteCompleteForm?.success) {
                message.success('Form deleted successfully');
                refetch();
            } else {
                message.error(data?.deleteCompleteForm?.message || 'Failed to delete form');
            }
        },
        onError: (error) => {
            console.error('Error deleting form:', error);
            message.error('Error deleting form');
        }
    });

    const [deleteQuestionOptionInForm] = useMutation(Delete_Question_Option_In_Form, {
        onCompleted: (data) => {
            if (data?.deleteQuestionOptionInForm?.success) {
                message.success('Question deleted successfully');
            } else {
                message.error(data?.deleteQuestionOptionInForm?.message || 'Failed to delete question');
            }
        },
        onError: (error) => {
            console.error('Error deleting question:', error);
            message.error('Error deleting question');
        }
    });

    const handleBackToViewRole = () => {
        const safeRoleId = roleId || location.state?.roleId;
        const safeAccountName = accountName || location.state?.accountName;

        navigate("/view-role", {
            state: {
                roleId: safeRoleId,
                accountId: accountId || location.state?.accountId,
                accountName: safeAccountName,
                name: name || location.state?.name,
                dartsNeeded: dartsNeeded || location.state?.dartsNeeded,
                clanNeeded: clanNeeded || location.state?.clanNeeded,
                syncNeeded: syncNeeded || location.state?.syncNeeded,
                dartsFormNeeded: dartsFormNeeded || location.state?.dartsFormNeeded,
                pocName: pocName || location.state?.pocName,
                pocEmail: pocEmail || location.state?.pocEmail,
                pocNumber: pocNumber || location.state?.pocNumber,
                numberLogin: numberLogin || location.state?.numberLogin,
                emailLogin: emailLogin || location.state?.emailLogin,
                mandatoryTimeDistribution: mandatoryTimeDistribution || location.state?.mandatoryTimeDistribution,
                targetEditDays: targetEditDays || location.state?.targetEditDays,
                inputEditDays: inputEditDays || location.state?.inputEditDays,
            },
        });
    };

    const [expandedFields, setExpandedFields] = useState({});

    useEffect(() => {
        if (!apiLoading) {
            if (formDetailsData?.getFormDetails?.data) {
                const transformedForms = formDetailsData.getFormDetails.data.map(form => ({
                    id: form.id,
                    name: form.formName,
                    description: '',
                    fields: [],
                    requiredStarQuestions: form.requiredStarQuestions || 0,
                    duplicateCheckLevel: form.duplicateCheckLevel || 'USER',
                    duplicateCheckQuestionIds: form.duplicateCheckQuestionIds || []
                }));
                setForms(transformedForms);
                setPageMeta(formDetailsData.getFormDetails.pageMeta);
            } else {
                setForms([]);
                setPageMeta(null);
            }
            setLoading(false);
        }
    }, [formDetailsData, apiLoading]);

    // Reset pagination when search term changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    // Fetch question types and answer types only when questions view is shown
    useEffect(() => {
        if (view === 'questions') {
            fetchQuestionTypes();
            fetchAnswerTypes();
            // Fetch all questions for this form to autofill
            if (currentFormId) {
                fetchAllFormQuestions({
                    variables: { formId: parseInt(currentFormId) }
                });
            }
        }
    }, [view, currentFormId]);

    // Populate questions when data is fetched
    useEffect(() => {
        if (allFormQuestionsData?.getAllFormQuestionAndOptions && view === 'questions') {
            const fetchedQuestions = allFormQuestionsData.getAllFormQuestionAndOptions.map(q => ({
                id: q.id,
                fieldName: q.question,
                questionType: q.qType,
                answerType: q.aType,
                starQuestionRequired: q.starQuestion === 1,
                hintText: q.hintText || '',
                maxLines: q.maxLines || '',
                reValidation: q.reValidation || '',
                reValidationText: q.reValidationText || '',
                contributingToFormDate: q.contributingToFormDate === 1 || q.contributingToFormDate === true,
                options: q.options ? q.options.map(opt => ({ id: opt.id, option: opt.option || opt.name || '' })) : [],
                questionId: q.id
            }));
            setQuestions(fetchedQuestions);
            // Auto-expand all questions when loaded from API
            const expandedState = {};
            fetchedQuestions.forEach(q => {
                expandedState[q.id] = true;
            });
            setExpandedQuestions(expandedState);

            // Validate fetched questions and show errors for empty fields
            const allErrors = {};
            fetchedQuestions.forEach(question => {
                const errors = {};
                if (!question.fieldName || !question.fieldName.trim()) {
                    errors.fieldName = 'Field name is required';
                }
                if (!question.questionType) {
                    errors.questionType = 'Question type is required';
                }
                if (!question.answerType) {
                    errors.answerType = 'Answer type is required';
                }
                if (question.starQuestionRequired === null || question.starQuestionRequired === undefined) {
                    errors.starQuestionRequired = 'Please select if this is a mandatory question';
                }
                // if (!question.reValidation || !question.reValidation.trim()) {
                //     errors.reValidation = 'Regex validation is required';
                // }
                if (question.contributingToFormDate === null || question.contributingToFormDate === undefined) {
                    errors.contributingToFormDate = 'Please select if this question contributes to form submission date';
                }
                // Validate Options if Question Type requires options
                const requiresOptions = question.questionType &&
                    (question.questionType.toLowerCase() === 'radio' ||
                        question.questionType.toLowerCase() === 'select');
                if (requiresOptions && (!question.options || question.options.length === 0 || !question.options.some(opt => opt.option && opt.option.trim()))) {
                    errors.options = 'At least one option is required';
                }
                if (Object.keys(errors).length > 0) {
                    allErrors[question.id] = errors;
                }
            });
            setEditQuestionErrors(allErrors);
        }
    }, [allFormQuestionsData, view]);

    const handleAddForm = () => {
        setEditingForm({
            name: '',
            description: '',
            fields: []
        });
        setView('add');
        setExpandedFields({});
        setShowDuplicateCheck(false);
        setDuplicateCheckLevel('USER');
        setDuplicateCheckQuestions([]);
    };

    const handleEditForm = (form) => {
        setSelectedForm(form);
        setEditingForm(JSON.parse(JSON.stringify(form)));
        setCurrentFormId(form.id);
        setCurrentFormName(form.name);
        setView('edit');
        setExpandedFields({});
        setIsFormModified(false);

        // Show duplicate check section if requiredStarQuestions > 0
        if (form.requiredStarQuestions > 0) {
            setShowDuplicateCheck(true);
            setDuplicateCheckLevel(form.duplicateCheckLevel || 'USER');
            setDuplicateCheckQuestions(form.duplicateCheckQuestionIds || []);
            // Fetch questions for this form
            fetchFormQuestions({
                variables: {
                    formId: parseInt(form.id),
                    onlyStar: false
                }
            });
        } else {
            setShowDuplicateCheck(false);
            setDuplicateCheckLevel('USER');
            setDuplicateCheckQuestions([]);
        }
    };

    const handleDeleteForm = (id) => {
        setFormToDelete(id);
        setShowDeleteModal(true);
    };

    const confirmDeleteForm = async () => {
        if (!formToDelete) return;
        setDeleteLoading(true);
        try {
            await deleteCompleteForm({
                variables: {
                    roleId: parseInt(roleId),
                    formId: parseInt(formToDelete)
                }
            });
            setShowDeleteModal(false);
            setFormToDelete(null);
        } catch (error) {
            console.error('Error deleting form:', error);
        } finally {
            setDeleteLoading(false);
        }
    };

    const cancelDeleteForm = () => {
        setShowDeleteModal(false);
        setFormToDelete(null);
    };

    const handleSaveForm = async () => {
        if (!editingForm.name.trim()) {
            setFormErrors({ name: 'Form name is required' });
            return;
        }

        setSaveLoading(true);
        try {
            await addUpdateForm({
                variables: {
                    formName: editingForm.name,
                    roleId: parseInt(roleId),
                    formId: view === 'edit' ? editingForm.id : null,
                    parentFormId: null,
                    duplicateCheckLevel: duplicateCheckLevel || null,
                    duplicateCheckQuestionIds: duplicateCheckQuestions.length > 0 ? duplicateCheckQuestions.map(id => parseInt(id)) : [],
                    requiredStarQuestions: duplicateCheckQuestions.length > 0 ? duplicateCheckQuestions.length : 0
                }
            });
        } catch (error) {
            console.error('Error saving form:', error);
        } finally {
            setSaveLoading(false);
        }
    };

    const handleCancel = () => {
        setView('list');
        setEditingForm({ name: '', description: '', fields: [] });
        setSelectedForm(null);
        setFormErrors({});
        setFieldErrors({});
        setShowDuplicateCheck(false);
    };

    const handleAddQuestion = () => {
        // Check if already have 5 questions
        if (questions.length >= 5) {
            message.error('more than 5 is not accepted');
            return;
        }

        const errors = {};

        if (!editingQuestion.fieldName.trim()) {
            errors.fieldName = 'Field name is required';
        }
        if (!editingQuestion.questionType) {
            errors.questionType = 'Question type is required';
        }
        if (!editingQuestion.answerType) {
            errors.answerType = 'Answer type is required';
        }
        if (editingQuestion.starQuestionRequired === null || editingQuestion.starQuestionRequired === undefined) {
            errors.starQuestionRequired = 'Please select if this is a mandatory question';
        }
        // if (!editingQuestion.reValidation || !editingQuestion.reValidation.trim()) {
        //     errors.reValidation = 'Regex validation is required';
        // }
        if (editingQuestion.contributingToFormDate === null || editingQuestion.contributingToFormDate === undefined) {
            errors.contributingToFormDate = 'Please select if this question contributes to form submission date';
        }
        // Validate Options if Question Type requires options
        const requiresOptions = editingQuestion.questionType &&
            (editingQuestion.questionType.toLowerCase() === 'radio' ||
                editingQuestion.questionType.toLowerCase() === 'select' ||
                editingQuestion.questionType.toLowerCase() === 'dropdown' ||
                editingQuestion.questionType.toLowerCase() === 'multiselectradio' ||
                editingQuestion.questionType.toLowerCase() === 'checkbox');
        if (requiresOptions && (!editingQuestion.options || editingQuestion.options.length === 0 || !editingQuestion.options.some(opt => opt.option && opt.option.trim()))) {
            errors.options = 'At least one option is required';
        }

        setQuestionErrors(errors);

        if (Object.keys(errors).length > 0) {
            return;
        }

        const newQuestion = {
            id: Date.now(),
            ...editingQuestion
        };
        setQuestions([...questions, newQuestion]);
        // Collapse all existing questions including the newly added one
        const collapsedState = {};
        questions.forEach(q => { collapsedState[q.id] = false; });
        collapsedState[newQuestion.id] = false;
        setExpandedQuestions(collapsedState);
        setQuestionErrors({});
        setEditingQuestion({
            fieldName: '',
            questionType: null,
            answerType: null,
            starQuestionRequired: null,
            hintText: '',
            maxLines: '',
            reValidation: '',
            reValidationText: '',
            contributingToFormDate: null,
            options: [],
            questionId: null
        });
    };

    const handleDeleteQuestion = (questionId) => {
        setQuestionToDelete(questionId);
        setShowDeleteQuestionModal(true);
    };

    const confirmDeleteQuestion = async () => {
        if (!questionToDelete) return;

        // Find the question to check if it has a questionId (from API)
        const question = questions.find(q => q.id === questionToDelete);

        setDeleteQuestionLoading(true);

        // If question has a questionId (exists in DB), call the delete API
        if (question?.questionId) {
            try {
                await deleteQuestionOptionInForm({
                    variables: {
                        questionId: parseInt(question.questionId),
                        formId: parseInt(currentFormId),
                        referenceId: null
                    }
                });
            } catch (error) {
                console.error('Error deleting question:', error);
                setDeleteQuestionLoading(false);
                return;
            }
        }

        // Remove from local state
        setQuestions(questions.filter(q => q.id !== questionToDelete));
        setShowDeleteQuestionModal(false);
        setQuestionToDelete(null);
        setDeleteQuestionLoading(false);
    };

    const cancelDeleteQuestion = () => {
        setShowDeleteQuestionModal(false);
        setQuestionToDelete(null);
    };

    const handleQuestionsCancel = () => {
        setView('list');
        setQuestions([]);
        setEditingQuestion({
            fieldName: '',
            questionType: null,
            answerType: null,
            starQuestionRequired: null,
            hintText: '',
            maxLines: '',
            reValidation: '',
            reValidationText: '',
            contributingToFormDate: null,
            options: [],
            questionId: null
        });
        setCurrentFormId(null);
        setCurrentFormName('');
    };

    const [questionSaveLoading, setQuestionSaveLoading] = useState(false);

    const handleQuestionsSave = async () => {
        // Combine existing questions with editingQuestion if it has data
        let questionsToSave = [...questions];

        // If editingQuestion has fieldName filled, include it in the save
        if (editingQuestion.fieldName && editingQuestion.fieldName.trim()) {
            const newQuestion = {
                id: Date.now(),
                ...editingQuestion
            };
            questionsToSave = [...questionsToSave, newQuestion];
        }

        // If no questions to save, show error
        if (questionsToSave.length === 0) {
            message.error('Please add at least one question before saving');
            return;
        }

        // Validate all questions before saving
        const allErrors = {};
        let hasErrors = false;

        for (const question of questionsToSave) {
            const errors = {};

            if (!question.fieldName || !question.fieldName.trim()) {
                errors.fieldName = 'Field name is required';
                hasErrors = true;
            }
            if (!question.questionType) {
                errors.questionType = 'Question type is required';
                hasErrors = true;
            }
            if (!question.answerType) {
                errors.answerType = 'Answer type is required';
                hasErrors = true;
            }
            if (question.starQuestionRequired === null || question.starQuestionRequired === undefined) {
                errors.starQuestionRequired = 'Please select if this is a mandatory question';
                hasErrors = true;
            }
            // if (!question.reValidation || !question.reValidation.trim()) {
            //     errors.reValidation = 'Regex validation is required';
            //     hasErrors = true;
            // }
            if (question.contributingToFormDate === null || question.contributingToFormDate === undefined) {
                errors.contributingToFormDate = 'Please select if this question contributes to form submission date';
                hasErrors = true;
            }
            // Validate Options if Question Type requires options
            const requiresOptions = question.questionType &&
                (question.questionType.toLowerCase() === 'radio' ||
                    question.questionType.toLowerCase() === 'select');
            if (requiresOptions && (!question.options || question.options.length === 0 || !question.options.some(opt => opt.option && opt.option.trim()))) {
                errors.options = 'At least one option is required';
                hasErrors = true;
            }

            if (Object.keys(errors).length > 0) {
                allErrors[question.id] = errors;
            }
        }

        if (hasErrors) {
            setEditQuestionErrors(allErrors);
            // Expand all questions with errors
            const expandState = {};
            Object.keys(allErrors).forEach(id => {
                expandState[parseInt(id)] = true;
            });
            setExpandedQuestions(prev => ({ ...prev, ...expandState }));
            message.error('Please fix the validation errors before saving');
            return;
        }

        try {
            setQuestionSaveLoading(true);

            // Save each question
            for (const question of questionsToSave) {
                const questionData = {
                    formId: parseInt(currentFormId),
                    question: question.fieldName,
                    qType: question.questionType,
                    aType: question.answerType,
                    hintText: question.hintText || null,
                    starQuestion: question.starQuestionRequired ? 1 : 0,
                    maxLines: question.maxLines ? parseInt(question.maxLines) : 1,
                    reValidation: question.reValidation || null,
                    reValidationText: question.reValidationText || null,
                    contributingToFormDate: question.contributingToFormDate || false,
                    options: question.options?.length > 0 ? question.options.map(opt => ({
                        dependentQuestionId: null,
                        option: opt.option,
                        status: 1,
                        id: opt.id || null
                    })) : [],
                    questionId: question.questionId || null
                };

                await addUpdateQuestion({
                    variables: {
                        data: questionData
                    }
                });
            }

            message.success('Questions saved successfully');
            refetch();
            setView('list');
            setQuestions([]);
            setDuplicateCheckQuestions([]);
            setCurrentFormId(null);
            setCurrentFormName('');
            setEditingQuestion({
                fieldName: '',
                questionType: null,
                answerType: null,
                starQuestionRequired: null,
                hintText: '',
                maxLines: '',
                reValidation: '',
                reValidationText: '',
                contributingToFormDate: null,
                options: [],
                questionId: null
            });
        } catch (error) {
            console.error('Error saving questions:', error);
            message.error(`Error saving questions: ${error.message}. Please try again.`);
        } finally {
            setQuestionSaveLoading(false);
        }
    };

    const toggleDuplicateCheckQuestion = (questionId) => {
        if (duplicateCheckQuestions.includes(questionId)) {
            setDuplicateCheckQuestions(duplicateCheckQuestions.filter(q => q !== questionId));
        } else {
            setDuplicateCheckQuestions([...duplicateCheckQuestions, questionId]);
        }
        if (view === 'edit') setIsFormModified(true);
    };

    const handleDuplicateCheckNext = () => {
        setView('questions');
        setEditingForm({ name: '', description: '', fields: [] });
        setShowDuplicateCheck(false);
        setQuestionsFromEdit(true);
    };

    if (loading || apiLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="loading-container">
                        <div className="spinner-loader"></div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="user-management-container-stages">
             <div className="mb-4 -mt-[-10px]">
            <div className="flex items-center justify-between w-full px-2">

                {/* Back */}
                <button
                    onClick={() =>
                        navigate("/products-Configuration", {
                            state: fullState,
                        })
                    }
                    className="
                            flex items-center gap-2
                            bg-[#C21A17]
                            text-white
                            px-5 py-2
                            rounded-lg
                            text-sm
                            font-semibold
                            shadow-md
                            hover:bg-[#A91513]
                            transition-all
                            duration-200
                          "
                >
                    <img
                        src={BackIcon}
                        alt="Back"
                        className="w-[15px] h-[15px] object-contain"
                    />
                    Back
                </button>

                {/* Home */}
                <button
                    onClick={() => navigate("/admin")}
                    className="
                            flex items-center gap-2
                            bg-[#C21A17]
                            text-white
                            px-5 py-2
                            rounded-lg
                            text-sm
                            font-semibold
                            shadow-md
                            hover:bg-[#A91513]
                            transition-all
                            duration-200
                          "
                >
                    <img
                        src={HomeIcon}
                        alt="Home"
                        className="w-[15px] h-[15px] object-contain"
                    />
                    Home
                </button>

            </div>
            </div>

            <div className="tabs-container-stages">
                <span className="config-label" onClick={() => navigate('/productConfiguration', { state: { name, accountName, roleId, dartsFormNeeded, dartsNeeded, roleId, accountId, clanNeeded, syncNeeded } })}>
                    <span>LDMS Configuration</span>
                </span>
                <span className="tabs-stages">
                   
                    <button
                        className="tab-btn"
                        onClick={() => navigate('/statuses', { state: { name, accountName, roleId, dartsFormNeeded, dartsNeeded, roleId, accountId, clanNeeded, syncNeeded } })}
                    >
                        Statuses
                    </button>
                    <button
                        className="tab-btn"
                        onClick={() => navigate('/stages', { state: { name, accountName, roleId, dartsFormNeeded, dartsNeeded, roleId, accountId, clanNeeded, syncNeeded } })}
                    >
                        Stages
                    </button>
                    <button
                        className="tab-btn active"
                    >
                        Forms Configuration
                    </button>

                     <button
                        className="tab-btn"
                        onClick={() => navigate('/activities', { state: { name, accountName, roleId, dartsFormNeeded, dartsNeeded, roleId, accountId, clanNeeded, syncNeeded } })}
                    >
                        Activities
                    </button>
                </span>
            </div>

            <div className="min-h-screen">
                {/* Header */}
                {view === 'list' && (
                    <div className="flex items-center gap-3 font-inter">
                        <Input
                            placeholder="Search forms..."
                            suffix={<SearchOutlined style={{ color: '#999' }} />}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="flex-1 h-11 rounded-lg"
                        />
                        <button
                            onClick={handleAddForm}
                            className="flex items-center gap-2 bg-[#AD0101] text-white px-4 py-[9px] rounded-[4px] hover:bg-[#AD0101] transition-colors whitespace-nowrap"
                        >
                            Add New Form
                        </button>
                    </div>
                )}

                <div style={{ paddingTop: '15px' }}>
                    {view === 'list' ? (
                        /* List View */
                        (() => {
                            const filteredForms = searchTerm
                                ? forms.filter(form =>
                                    form.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                    form.description.toLowerCase().includes(searchTerm.toLowerCase())
                                )
                                : forms;

                            const handleNextPage = () => {
                                if (pageMeta?.hasNextPage && pageMeta?.nextPage) {
                                    setCursor(pageMeta.nextPage);
                                    setCurrentPage(currentPage + 1);
                                }
                            };

                            const handlePreviousPage = () => {
                                if (pageMeta?.hasPreviousPage && pageMeta?.previousPage) {
                                    setCursor(pageMeta.previousPage);
                                    setCurrentPage(currentPage - 1);
                                }
                            };

                            return (
                                <div className="grid gap-4">
                                    {filteredForms.length === 0 ? (
                                        <div className="bg-white rounded-lg shadow p-12 text-center">
                                            <p className="text-gray-500 text-lg">No forms configured yet</p>
                                        </div>
                                    ) : (
                                        <>
                                            {filteredForms.map(form => (
                                                <div key={form.id} className="relative flex min-h-[64px] items-center justify-center bg-white rounded-lg shadow hover:shadow-md transition-shadow">
                                                    <div className="w-full px-6 py-[-0.5] text-left">
                                                        <h3 className="text-base font-medium text-gray-900">{form.name}</h3>
                                                        {form.description && <p className="mt-1 text-sm text-gray-600">{form.description}</p>}
                                                    </div>
                                                    <div className="absolute right-3 top-1/2 flex -translate-y-1/2 gap-2">
                                                                <button
                                                                    onClick={() => handleEditForm(form)}
                                                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                                    title="Edit form"
                                                                >
                                                                    <img src={Editicon} alt="Edit" style={{ width: '18px', height: '18px' }} />
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDeleteForm(form.id)}
                                                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                                    title="Delete form"
                                                                >
                                                                    <Trash2 size={18} />
                                                                </button>
                                                    </div>
                                                </div>
                                            ))}
                                            {pageMeta && pageMeta.totalCount > pageSize && (
                                                <div className="flex justify-end mt-4">
                                                    <Pagination
                                                        current={currentPage}
                                                        pageSize={pageSize}
                                                        total={pageMeta.totalCount}
                                                        onChange={(page) => {
                                                            if (page > currentPage && pageMeta?.hasNextPage) {
                                                                setCursor(pageMeta.nextPage);
                                                            } else if (page < currentPage && pageMeta?.hasPreviousPage) {
                                                                setCursor(pageMeta.previousPage);
                                                            }
                                                            setCurrentPage(page);
                                                        }}
                                                        showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
                                                        showSizeChanger={false}
                                                    />
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            );
                        })()
                    ) : view === 'questions' ? (
                        /* Questions View */
                        <div className="bg-white rounded-lg shadow">
                            <div className="p-3 border-b border-gray-200 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <button
                                        onClick={() => setView(questionsFromEdit ? 'edit' : 'add')}
                                        className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                                    >
                                        Back
                                    </button>
                                </div>
                                <button
                                    onClick={() => {
                                        if (questions.length >= 5) {
                                            message.error('More than 5 is not accepted');
                                        } else if (questionsFromEdit) {
                                            // Validate all existing questions before adding a new one
                                            let hasErrors = false;
                                            const allErrors = {};

                                            questions.forEach(q => {
                                                const errors = {};
                                                if (!q.fieldName || !q.fieldName.trim()) {
                                                    errors.fieldName = 'Field name is required';
                                                }
                                                if (!q.questionType) {
                                                    errors.questionType = 'Question type is required';
                                                }
                                                if (!q.answerType) {
                                                    errors.answerType = 'Answer type is required';
                                                }
                                                if (q.starQuestionRequired === null || q.starQuestionRequired === undefined) {
                                                    errors.starQuestionRequired = 'Please select if this is a mandatory question';
                                                }
                                                // if (!q.reValidation || !q.reValidation.trim()) {
                                                //     errors.reValidation = 'Regex validation is required';
                                                // }
                                                if (q.contributingToFormDate === null || q.contributingToFormDate === undefined) {
                                                    errors.contributingToFormDate = 'Please select if this question contributes to form submission date';
                                                }
                                                // Validate Options if Question Type requires options
                                                const type = editingQuestion.questionType?.toLowerCase().trim();

                                                const requiresOptions =
                                                    type === 'radio' ||
                                                    type === 'select' ||
                                                    type === 'dropdown' ||
                                                    type === 'multiselectradio' ||
                                                    type === 'checkbox';

                                                if (
                                                    requiresOptions &&
                                                    (!editingQuestion.options ||
                                                        editingQuestion.options.length === 0 ||
                                                        !editingQuestion.options.some(opt => opt.option && opt.option.trim()))
                                                ) {
                                                    errors.options = 'At least one option is required';
                                                }
                                                if (Object.keys(errors).length > 0) {
                                                    hasErrors = true;
                                                    allErrors[q.id] = errors;
                                                }
                                            });

                                            if (hasErrors) {
                                                setEditQuestionErrors(allErrors);
                                                // Expand all questions with errors
                                                const expandState = {};
                                                Object.keys(allErrors).forEach(id => {
                                                    expandState[id] = true;
                                                });
                                                setExpandedQuestions(prev => ({ ...prev, ...expandState }));
                                                return;
                                            }

                                            // All validations passed, collapse all existing questions and add new expanded question
                                            const newQuestion = {
                                                id: Date.now(),
                                                fieldName: '',
                                                questionType: null,
                                                answerType: null,
                                                starQuestionRequired: false,
                                                hintText: '',
                                                maxLines: '',
                                                reValidation: '',
                                                reValidationText: '',
                                                contributingToFormDate: false,
                                                options: [],
                                                questionId: null
                                            };
                                            setQuestions([...questions, newQuestion]);
                                            // Collapse all existing questions and expand only the new one
                                            const collapsedState = {};
                                            questions.forEach(q => { collapsedState[q.id] = false; });
                                            collapsedState[newQuestion.id] = true;
                                            setExpandedQuestions(collapsedState);
                                        } else {
                                            handleAddQuestion();
                                        }
                                    }}
                                    className="flex items-center gap-2 px-4 py-2 border border-[#AD0101] text-[#AD0101] rounded-lg hover:bg-red-50 transition-colors"
                                >
                                    Add Question
                                </button>
                            </div>

                            <div className="p-6 space-y-6">
                                {/* Added Questions List */}
                                {questions.length > 0 && (
                                    <div className="space-y-3 mb-6">
                                        {/* <h4 className="text-sm font-medium text-gray-700">Added Question{questions.length > 1 ? 's' : ''}</h4> */}
                                        {questions.map((q, index) => (
                                            <div key={q.id} className="bg-gray-50 rounded-lg border border-gray-200">
                                                <div className="flex items-center justify-between p-3">
                                                    <div className="flex-1">
                                                        <span className="font-medium">{index + 1}. {q.fieldName}</span>
                                                        <div className="text-sm text-gray-500 mt-1">
                                                            Type: {questionTypes.find(t => t.value === q.questionType)?.label || '-'} |
                                                            Answer: {answerTypes.find(t => t.value === q.answerType)?.label || '-'}
                                                            {q.starQuestionRequired && ' | ⭐ Required'}
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <button
                                                            onClick={() => setExpandedQuestions(prev => ({ ...prev, [q.id]: !prev[q.id] }))}
                                                            className="p-2 text-gray-600 hover:bg-gray-200 rounded-lg"
                                                        >
                                                            {expandedQuestions[q.id] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteQuestion(q.id)}
                                                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                                        >
                                                            <Trash2 size={16} />
                                                        </button>
                                                    </div>
                                                </div>
                                                {expandedQuestions[q.id] && (
                                                    <div className="p-4 border-t border-gray-200 space-y-4">
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">Field Name <span className='text-[#AD0101]'>*</span></label>
                                                            <input
                                                                type="text"
                                                                value={q.fieldName}
                                                                placeholder='Enter field name'
                                                                onChange={(e) => {
                                                                    setQuestions(questions.map(item => item.id === q.id ? { ...item, fieldName: e.target.value } : item));
                                                                    setIsQuestionsModified(true);
                                                                    if (editQuestionErrors[q.id]?.fieldName) {
                                                                        setEditQuestionErrors({ ...editQuestionErrors, [q.id]: { ...editQuestionErrors[q.id], fieldName: null } });
                                                                    }
                                                                }}
                                                                className={`w-full px-3 py-2 border ${editQuestionErrors[q.id]?.fieldName ? 'border-[#AD0101]' : 'border-gray-300'} rounded-lg`}
                                                            />
                                                            {editQuestionErrors[q.id]?.fieldName && <p className="text-[#AD0101] text-sm mt-1">{editQuestionErrors[q.id].fieldName}</p>}
                                                        </div>
                                                        <div className="grid grid-cols-2 gap-4">
                                                            <div>
                                                                <label className="block text-sm font-medium text-gray-700 mb-2">Question Type <span className='text-[#AD0101]'>*</span></label>
                                                                <Select
                                                                    value={q.questionType}
                                                                    onChange={(value) => {
                                                                        setQuestions(questions.map(item => item.id === q.id ? { ...item, questionType: value } : item));
                                                                        setIsQuestionsModified(true);
                                                                        if (editQuestionErrors[q.id]?.questionType) {
                                                                            setEditQuestionErrors({ ...editQuestionErrors, [q.id]: { ...editQuestionErrors[q.id], questionType: null } });
                                                                        }
                                                                    }}
                                                                    className="w-full"
                                                                    placeholder="Select question type"
                                                                    options={questionTypes}
                                                                />
                                                                {editQuestionErrors[q.id]?.questionType && <p className="text-[#AD0101] text-sm mt-1">{editQuestionErrors[q.id].questionType}</p>}
                                                            </div>
                                                            <div>
                                                                <label className="block text-sm font-medium text-gray-700 mb-2">Answer Type <span className='text-[#AD0101]'>*</span></label>
                                                                <Select
                                                                    value={q.answerType}
                                                                    onChange={(value) => {
                                                                        setQuestions(questions.map(item => item.id === q.id ? { ...item, answerType: value } : item));
                                                                        setIsQuestionsModified(true);
                                                                        if (editQuestionErrors[q.id]?.answerType) {
                                                                            setEditQuestionErrors({ ...editQuestionErrors, [q.id]: { ...editQuestionErrors[q.id], answerType: null } });
                                                                        }
                                                                    }}
                                                                    className="w-full"
                                                                    placeholder="Select answer type"
                                                                    options={answerTypes}
                                                                />
                                                                {editQuestionErrors[q.id]?.answerType && <p className="text-[#AD0101] text-sm mt-1">{editQuestionErrors[q.id].answerType}</p>}
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">Hint Text</label>
                                                            <input
                                                                type="text"
                                                                value={q.hintText || ''}
                                                                placeholder="Enter hint text (optional)"
                                                                onChange={(e) => { setQuestions(questions.map(item => item.id === q.id ? { ...item, hintText: e.target.value } : item)); setIsQuestionsModified(true); }}
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                            />
                                                        </div>

                                                        <div className="grid grid-cols-2 gap-4">

                                                            <div>
                                                                <label className="block text-sm font-medium text-gray-700 mb-2">Is this a mandatory question? <span className='text-[#AD0101]'>*</span></label>
                                                                <div className="flex gap-6">
                                                                    <label className="flex items-center cursor-pointer">
                                                                        <input
                                                                            type="radio"
                                                                            name={`starQuestionRequired_${q.id}`}
                                                                            checked={q.starQuestionRequired === true}
                                                                            onChange={() => {
                                                                                setQuestions(questions.map(item => item.id === q.id ? { ...item, starQuestionRequired: true } : item));
                                                                                setIsQuestionsModified(true);
                                                                                if (editQuestionErrors[q.id]?.starQuestionRequired) {
                                                                                    setEditQuestionErrors({ ...editQuestionErrors, [q.id]: { ...editQuestionErrors[q.id], starQuestionRequired: null } });
                                                                                }
                                                                            }}
                                                                            className="w-4 h-4 text-[#AD0101] border-gray-300"
                                                                        />
                                                                        <span className="ml-2 text-sm text-gray-700">True</span>
                                                                    </label>
                                                                    <label className="flex items-center cursor-pointer">
                                                                        <input
                                                                            type="radio"
                                                                            name={`starQuestionRequired_${q.id}`}
                                                                            checked={q.starQuestionRequired === false}
                                                                            onChange={() => {
                                                                                setQuestions(questions.map(item => item.id === q.id ? { ...item, starQuestionRequired: false } : item));
                                                                                setIsQuestionsModified(true);
                                                                                if (editQuestionErrors[q.id]?.starQuestionRequired) {
                                                                                    setEditQuestionErrors({ ...editQuestionErrors, [q.id]: { ...editQuestionErrors[q.id], starQuestionRequired: null } });
                                                                                }
                                                                            }}
                                                                            className="w-4 h-4 text-[#AD0101] border-gray-300"
                                                                        />
                                                                        <span className="ml-2 text-sm text-gray-700">False</span>
                                                                    </label>
                                                                </div>
                                                                {editQuestionErrors[q.id]?.starQuestionRequired && <p className="text-[#AD0101] text-sm mt-1">{editQuestionErrors[q.id].starQuestionRequired}</p>}
                                                            </div>

                                                            <div>
                                                                <label className="block text-sm font-medium text-gray-700 mb-2">Does this question contribute to the form submission date? <span className='text-[#AD0101]'>*</span></label>
                                                                <div className="flex gap-6">
                                                                    <label className="flex items-center cursor-pointer">
                                                                        <input
                                                                            type="radio"
                                                                            name={`contributingToFormDate_${q.id}`}
                                                                            checked={q.contributingToFormDate === true}
                                                                            onChange={() => {
                                                                                setQuestions(questions.map(item => item.id === q.id ? { ...item, contributingToFormDate: true } : item));
                                                                                setIsQuestionsModified(true);
                                                                                if (editQuestionErrors[q.id]?.contributingToFormDate) {
                                                                                    setEditQuestionErrors({ ...editQuestionErrors, [q.id]: { ...editQuestionErrors[q.id], contributingToFormDate: null } });
                                                                                }
                                                                            }}
                                                                            className="w-4 h-4 text-[#AD0101] border-gray-300"
                                                                        />
                                                                        <span className="ml-2 text-sm text-gray-700">True</span>
                                                                    </label>
                                                                    <label className="flex items-center cursor-pointer">
                                                                        <input
                                                                            type="radio"
                                                                            name={`contributingToFormDate_${q.id}`}
                                                                            checked={q.contributingToFormDate === false}
                                                                            onChange={() => {
                                                                                setQuestions(questions.map(item => item.id === q.id ? { ...item, contributingToFormDate: false } : item));
                                                                                setIsQuestionsModified(true);
                                                                                if (editQuestionErrors[q.id]?.contributingToFormDate) {
                                                                                    setEditQuestionErrors({ ...editQuestionErrors, [q.id]: { ...editQuestionErrors[q.id], contributingToFormDate: null } });
                                                                                }
                                                                            }}
                                                                            className="w-4 h-4 text-[#AD0101] border-gray-300"
                                                                        />
                                                                        <span className="ml-2 text-sm text-gray-700">False</span>
                                                                    </label>
                                                                </div>
                                                                {editQuestionErrors[q.id]?.contributingToFormDate && <p className="text-[#AD0101] text-sm mt-1">{editQuestionErrors[q.id].contributingToFormDate}</p>}
                                                            </div>
                                                        </div>

                                                        <div className="grid grid-cols-2 gap-4">
                                                            <div>
                                                                <label className="block text-sm font-medium text-gray-700 mb-2">Max Lines</label>
                                                                <input
                                                                    type="number"
                                                                    value={q.maxLines || ''}
                                                                    onChange={(e) => { setQuestions(questions.map(item => item.id === q.id ? { ...item, maxLines: parseInt(e.target.value) || '' } : item)); setIsQuestionsModified(true); }}
                                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                                    placeholder="Enter max lines"
                                                                />
                                                            </div>
                                                            <div>
                                                                <label className="block text-sm font-medium text-gray-700 mb-2">Regex validation
                                                                    {/* <span className='text-[#AD0101]'>*</span> */}
                                                                </label>
                                                                <input
                                                                    type="text"
                                                                    value={q.reValidation || ''}
                                                                    onChange={(e) => {
                                                                        setQuestions(questions.map(item => item.id === q.id ? { ...item, reValidation: e.target.value } : item));
                                                                        setIsQuestionsModified(true);
                                                                        if (editQuestionErrors[q.id]?.reValidation) {
                                                                            setEditQuestionErrors({ ...editQuestionErrors, [q.id]: { ...editQuestionErrors[q.id], reValidation: null } });
                                                                        }
                                                                    }}
                                                                    className={`w-full px-3 py-2 border ${editQuestionErrors[q.id]?.reValidation ? 'border-[#AD0101]' : 'border-gray-300'} rounded-lg`}
                                                                    placeholder="Enter regex validation"
                                                                />
                                                                {/* {editQuestionErrors[q.id]?.reValidation && <p className="text-[#AD0101] text-sm mt-1">{editQuestionErrors[q.id].reValidation}</p>} */}
                                                            </div>
                                                        </div>

                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">Regex Validation Text</label>
                                                            <input
                                                                type="text"
                                                                value={q.reValidationText || ''}
                                                                onChange={(e) => { setQuestions(questions.map(item => item.id === q.id ? { ...item, reValidationText: e.target.value } : item)); setIsQuestionsModified(true); }}
                                                                className="w-full px-3 py-3 border border-gray-300 rounded-lg"
                                                                placeholder="Enter regex validation text"
                                                            />
                                                        </div>

                                                        {(q.questionType?.toLowerCase() === 'radio' || q.questionType?.toLowerCase() === 'select' || q.questionType?.toLowerCase() === 'dropdown' || q.questionType?.toLowerCase() === 'multiselectradio' || q.questionType?.toLowerCase() === 'checkbox') && (
                                                            <div>
                                                                <label className="block text-sm font-medium text-gray-700 mb-2">Options <span className='text-[#AD0101]'>*</span></label>
                                                                <div className="space-y-2">
                                                                    {(q.options || []).map((opt, idx) => (
                                                                        <div key={idx} className="flex items-center gap-2">
                                                                            <input
                                                                                type="text"
                                                                                value={opt.option || ''}
                                                                                onChange={(e) => {
                                                                                    const newOptions = [...(q.options || [])];
                                                                                    newOptions[idx] = { ...newOptions[idx], option: e.target.value };
                                                                                    setQuestions(questions.map(item => item.id === q.id ? { ...item, options: newOptions } : item));
                                                                                    setIsQuestionsModified(true);
                                                                                    if (editQuestionErrors[q.id]?.options) {
                                                                                        setEditQuestionErrors({ ...editQuestionErrors, [q.id]: { ...editQuestionErrors[q.id], options: null } });
                                                                                    }
                                                                                }}
                                                                                className={`flex-1 px-3 py-2 border ${editQuestionErrors[q.id]?.options ? 'border-[#AD0101]' : 'border-gray-300'} rounded-lg`}
                                                                                placeholder={`Option ${idx + 1}`}
                                                                            />
                                                                            <button
                                                                                onClick={() => {
                                                                                    const newOptions = (q.options || []).filter((_, i) => i !== idx);
                                                                                    setQuestions(questions.map(item => item.id === q.id ? { ...item, options: newOptions } : item));
                                                                                    setIsQuestionsModified(true);
                                                                                }}
                                                                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                                                                title="Delete option"
                                                                            >
                                                                                <Trash2 size={16} />
                                                                            </button>
                                                                        </div>
                                                                    ))}
                                                                    <button
                                                                        onClick={() => {
                                                                            const newOptions = [...(q.options || []), { option: '' }];
                                                                            setQuestions(questions.map(item => item.id === q.id ? { ...item, options: newOptions } : item));
                                                                            setIsQuestionsModified(true);
                                                                            if (editQuestionErrors[q.id]?.options) {
                                                                                setEditQuestionErrors({ ...editQuestionErrors, [q.id]: { ...editQuestionErrors[q.id], options: null } });
                                                                            }
                                                                        }}
                                                                        className="flex items-center gap-2 px-3 py-2 text-sm text-[#AD0101] border border-dashed border-[#AD0101] rounded-lg hover:bg-red-50"
                                                                    >
                                                                        Add Option
                                                                    </button>
                                                                </div>
                                                                {editQuestionErrors[q.id]?.options && <p className="text-[#AD0101] text-sm mt-1">{editQuestionErrors[q.id].options}</p>}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Question Form - only show for new forms, not when editing */}
                                {questions.length < 5 && !questionsFromEdit && (
                                    <div className="space-y-4 p-4 border border-gray-200 rounded-lg">
                                        <h4 className="text-sm font-medium text-gray-700">Question {questions.length + 1}</h4>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Field Name <span className='text-[#AD0101]'>*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={editingQuestion.fieldName}
                                                onChange={(e) => {
                                                    setEditingQuestion({ ...editingQuestion, fieldName: e.target.value });
                                                    setIsQuestionsModified(true);
                                                    if (questionErrors.fieldName) {
                                                        setQuestionErrors({ ...questionErrors, fieldName: null });
                                                    }
                                                }}
                                                className={`w-full px-3 py-2 border ${questionErrors.fieldName ? 'border-[#AD0101]' : 'border-gray-300'} rounded-lg`}
                                                placeholder="Enter field name"
                                            />
                                            {questionErrors.fieldName && <p className="text-[#AD0101] text-sm mt-1">{questionErrors.fieldName}</p>}
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Question Type <span className='text-[#AD0101]'>*</span>
                                                </label>
                                                <Select
                                                    value={editingQuestion.questionType}
                                                    onChange={(value) => {
                                                        setEditingQuestion({ ...editingQuestion, questionType: value });
                                                        setIsQuestionsModified(true);
                                                        if (questionErrors.questionType) {
                                                            setQuestionErrors({ ...questionErrors, questionType: null });
                                                        }
                                                    }}
                                                    className="w-full"
                                                    placeholder="Select question type"
                                                    options={questionTypes}
                                                    status={questionErrors.questionType ? 'error' : ''}
                                                />
                                                {questionErrors.questionType && <p className="text-[#AD0101] text-sm mt-1">{questionErrors.questionType}</p>}
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Answer Type <span className='text-[#AD0101]'>*</span>
                                                </label>
                                                <Select
                                                    value={editingQuestion.answerType}
                                                    onChange={(value) => {
                                                        setEditingQuestion({ ...editingQuestion, answerType: value });
                                                        setIsQuestionsModified(true);
                                                        if (questionErrors.answerType) {
                                                            setQuestionErrors({ ...questionErrors, answerType: null });
                                                        }
                                                    }}
                                                    className="w-full"
                                                    placeholder="Select answer type"
                                                    options={answerTypes}
                                                    status={questionErrors.answerType ? 'error' : ''}
                                                />
                                                {questionErrors.answerType && <p className="text-[#AD0101] text-sm mt-1">{questionErrors.answerType}</p>}
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Hint Text
                                            </label>
                                            <input
                                                type="text"
                                                value={editingQuestion.hintText}
                                                onChange={(e) => {
                                                    setEditingQuestion({ ...editingQuestion, hintText: e.target.value });
                                                    setIsQuestionsModified(true);
                                                }}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                placeholder="Enter hint text (optional)"
                                            />
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Is this a mandatory question? <span className='text-[#AD0101]'>*</span>
                                                </label>
                                                <div className="flex gap-6">
                                                    <label className="flex items-center cursor-pointer">
                                                        <input
                                                            type="radio"
                                                            name="starQuestionRequired"
                                                            checked={editingQuestion.starQuestionRequired === true}
                                                            onChange={() => {
                                                                setEditingQuestion({ ...editingQuestion, starQuestionRequired: true });
                                                                setIsQuestionsModified(true);
                                                                if (questionErrors.starQuestionRequired) {
                                                                    setQuestionErrors({ ...questionErrors, starQuestionRequired: null });
                                                                }
                                                            }}
                                                            className="w-4 h-4 text-[#AD0101] border-gray-300"
                                                        />
                                                        <span className="ml-2 text-sm text-gray-700">True</span>
                                                    </label>
                                                    <label className="flex items-center cursor-pointer">
                                                        <input
                                                            type="radio"
                                                            name="starQuestionRequired"
                                                            checked={editingQuestion.starQuestionRequired === false}
                                                            onChange={() => {
                                                                setEditingQuestion({ ...editingQuestion, starQuestionRequired: false });
                                                                setIsQuestionsModified(true);
                                                                if (questionErrors.starQuestionRequired) {
                                                                    setQuestionErrors({ ...questionErrors, starQuestionRequired: null });
                                                                }
                                                            }}
                                                            className="w-4 h-4 text-[#AD0101] border-gray-300"
                                                        />
                                                        <span className="ml-2 text-sm text-gray-700">False</span>
                                                    </label>
                                                </div>
                                                {questionErrors.starQuestionRequired && <p className="text-[#AD0101] text-sm mt-1">{questionErrors.starQuestionRequired}</p>}
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Does this question contribute to the form submission date? <span className='text-[#AD0101]'>*</span>
                                                </label>
                                                <div className="flex gap-6">
                                                    <label className="flex items-center cursor-pointer">
                                                        <input
                                                            type="radio"
                                                            name="contributingToFormDate"
                                                            checked={editingQuestion.contributingToFormDate === true}
                                                            onChange={() => {
                                                                setEditingQuestion({ ...editingQuestion, contributingToFormDate: true });
                                                                setIsQuestionsModified(true);
                                                                if (questionErrors.contributingToFormDate) {
                                                                    setQuestionErrors({ ...questionErrors, contributingToFormDate: null });
                                                                }
                                                            }}
                                                            className="w-4 h-4 text-[#AD0101] border-gray-300"
                                                        />
                                                        <span className="ml-2 text-sm text-gray-700">True</span>
                                                    </label>
                                                    <label className="flex items-center cursor-pointer">
                                                        <input
                                                            type="radio"
                                                            name="contributingToFormDate"
                                                            checked={editingQuestion.contributingToFormDate === false}
                                                            onChange={() => {
                                                                setEditingQuestion({ ...editingQuestion, contributingToFormDate: false });
                                                                setIsQuestionsModified(true);
                                                                if (questionErrors.contributingToFormDate) {
                                                                    setQuestionErrors({ ...questionErrors, contributingToFormDate: null });
                                                                }
                                                            }}
                                                            className="w-4 h-4 text-[#AD0101] border-gray-300"
                                                        />
                                                        <span className="ml-2 text-sm text-gray-700">False</span>
                                                    </label>
                                                </div>
                                                {questionErrors.contributingToFormDate && <p className="text-[#AD0101] text-sm mt-1">{questionErrors.contributingToFormDate}</p>}
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Max Lines
                                                </label>
                                                <input
                                                    type="number"
                                                    min=""
                                                    value={editingQuestion.maxLines}
                                                    onChange={(e) => {
                                                        setEditingQuestion({ ...editingQuestion, maxLines: parseInt(e.target.value) || "" });
                                                        setIsQuestionsModified(true);
                                                        if (questionErrors.maxLines) {
                                                            setQuestionErrors({ ...questionErrors, maxLines: null });
                                                        }
                                                    }}
                                                    className={`w-full px-3 py-2 border ${questionErrors.maxLines ? 'border-[#AD0101]' : 'border-gray-300'} rounded-lg`}
                                                    placeholder="Enter max lines"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Regex Validation
                                                    {/* <span className='text-[#AD0101]'>*</span> */}
                                                </label>
                                                <input
                                                    type="text"
                                                    value={editingQuestion.reValidation}
                                                    onChange={(e) => {
                                                        setEditingQuestion({ ...editingQuestion, reValidation: e.target.value });
                                                        setIsQuestionsModified(true);
                                                        if (questionErrors.reValidation) {
                                                            setQuestionErrors({ ...questionErrors, reValidation: null });
                                                        }
                                                    }}
                                                    className={`w-full px-3 py-2 border ${questionErrors.reValidation ? 'border-[#AD0101]' : 'border-gray-300'} rounded-lg`}
                                                    placeholder="Enter regex validation"
                                                />
                                                {/* {questionErrors.reValidation && <p className="text-[#AD0101] text-sm mt-1">{questionErrors.reValidation}</p>} */}
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Regex Validation Text
                                            </label>
                                            <input
                                                type="text"
                                                value={editingQuestion.reValidationText}
                                                onChange={(e) => { setEditingQuestion({ ...editingQuestion, reValidationText: e.target.value }); setIsQuestionsModified(true); }}
                                                className="w-full px-3 py-3 border border-gray-300 rounded-lg"
                                                placeholder="Enter regex validation text"
                                            />
                                        </div>

                                        {(editingQuestion.questionType?.toLowerCase() === 'radio' ||
                                            editingQuestion.questionType?.toLowerCase() === 'dropdown' ||
                                            editingQuestion.questionType?.toLowerCase() === 'multiselectradio' ||
                                            editingQuestion.questionType?.toLowerCase() === 'checkbox') && (
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        Options <span className='text-[#AD0101]'>*</span>
                                                    </label>
                                                    <div className="space-y-2">
                                                        {editingQuestion.options.map((opt, idx) => (
                                                            <div key={idx} className="flex items-center gap-2">
                                                                <input
                                                                    type="text"
                                                                    value={opt.option}
                                                                    onChange={(e) => {
                                                                        const newOptions = [...editingQuestion.options];
                                                                        newOptions[idx] = { ...newOptions[idx], option: e.target.value };
                                                                        setEditingQuestion({ ...editingQuestion, options: newOptions });
                                                                        setIsQuestionsModified(true);
                                                                        if (questionErrors.options) {
                                                                            setQuestionErrors({ ...questionErrors, options: null });
                                                                        }
                                                                    }}
                                                                    className={`flex-1 px-3 py-2 border ${questionErrors.options ? 'border-[#AD0101]' : 'border-gray-300'} rounded-lg`}
                                                                    placeholder={`Option ${idx + 1}`}
                                                                />
                                                                <button
                                                                    onClick={() => {
                                                                        const newOptions = editingQuestion.options.filter((_, i) => i !== idx);
                                                                        setEditingQuestion({ ...editingQuestion, options: newOptions });
                                                                        setIsQuestionsModified(true);
                                                                    }}
                                                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                                                    title="Delete option"
                                                                >
                                                                    <Trash2 size={16} />
                                                                </button>
                                                            </div>
                                                        ))}
                                                        <button
                                                            onClick={() => {
                                                                setEditingQuestion({ ...editingQuestion, options: [...editingQuestion.options, { option: '' }] });
                                                                setIsQuestionsModified(true);
                                                                if (questionErrors.options) {
                                                                    setQuestionErrors({ ...questionErrors, options: null });
                                                                }
                                                            }}
                                                            className="flex items-center gap-2 px-3 py-2 text-sm text-[#AD0101] border border-dashed border-[#AD0101] rounded-lg hover:bg-red-50"
                                                        >
                                                            Add Option
                                                        </button>
                                                    </div>
                                                    {questionErrors.options && <p className="text-[#AD0101] text-sm mt-1">{questionErrors.options}</p>}
                                                </div>
                                            )}
                                    </div>
                                )}

                                {/* Action Buttons */}
                                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
                                    <button
                                        onClick={handleQuestionsCancel}
                                        className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleQuestionsSave}
                                        disabled={questionSaveLoading || !isQuestionsModified}
                                        className={`flex items-center gap-2 text-white px-4 py-2 rounded-lg transition-colors ${(questionSaveLoading || !isQuestionsModified) ? 'bg-gray-400' : 'bg-[#AD0101] hover:bg-[#B00000]'}`}
                                        style={{ cursor: (questionSaveLoading || !isQuestionsModified) ? 'not-allowed' : 'pointer' }}
                                    >
                                        {questionSaveLoading ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : `Save Question${questions.length > 1 ? 's' : ''}`}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        /* Add/Edit View */
                        <div className="bg-white rounded-lg shadow">
                            <div className="p-3 border-b border-gray-200">
                                <span className="text-xl font-semibold text-gray-900">
                                    {view === 'add' ? 'Add New Form' : 'Edit Form'}
                                </span>
                            </div>

                            <div className="p-6 space-y-6">
                                {/* Form Basic Info */}
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Form Name <span className='text-[#AD0101]'>*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={editingForm.name}
                                            onChange={(e) => { setEditingForm({ ...editingForm, name: e.target.value }); if (view === 'edit') setIsFormModified(true); }}
                                            className={`w-full px-3 py-2 border rounded-lg ${formErrors.name ? 'border-[#AD0101]' : 'border-gray-300'}`}
                                            placeholder="Enter form name"
                                            disabled={showDuplicateCheck}
                                        />
                                        {formErrors.name && <p className="text-[#AD0101] text-sm mt-1">{formErrors.name}</p>}
                                    </div>

                                    {/* Inline Duplicate Check Settings - shown after Save Form */}
                                    {showDuplicateCheck && (
                                        <>
                                            <div className="pt-4 border-t border-gray-200">
                                                <h4 className="text-sm font-medium text-gray-700 mb-3">Duplicate Check Level</h4>
                                                <div className="flex gap-6">
                                                    <label className="flex items-center cursor-pointer">
                                                        <input
                                                            type="radio"
                                                            name="duplicateCheckLevelInline"
                                                            value="USER"
                                                            checked={duplicateCheckLevel === 'USER'}
                                                            onChange={(e) => { setDuplicateCheckLevel(e.target.value); if (view === 'edit') setIsFormModified(true); }}
                                                            className="w-4 h-4 text-[#AD0101] border-gray-300"
                                                        />
                                                        <span className="ml-2 text-sm text-gray-700">User</span>
                                                    </label>
                                                    <label className="flex items-center cursor-pointer">
                                                        <input
                                                            type="radio"
                                                            name="duplicateCheckLevelInline"
                                                            value="JOBROLE"
                                                            checked={duplicateCheckLevel === 'JOBROLE'}
                                                            onChange={(e) => { setDuplicateCheckLevel(e.target.value); if (view === 'edit') setIsFormModified(true); }}
                                                            className="w-4 h-4 text-[#AD0101] border-gray-300"
                                                        />
                                                        <span className="ml-2 text-sm text-gray-700">Jobrole</span>
                                                    </label>
                                                </div>
                                            </div>

                                            <div>
                                                <h4 className="text-sm font-medium text-gray-700 mb-3">Duplicate Check Question</h4>
                                                {formQuestionsLoading ? (
                                                    <div className="text-sm text-gray-500">Loading questions...</div>
                                                ) : formQuestionsList.length === 0 ? (
                                                    <div className="text-sm text-gray-500">No questions available</div>
                                                ) : (
                                                    <div className="grid grid-cols-4 gap-4">
                                                        {formQuestionsList.map((q) => (
                                                            <label key={q.id} className="flex items-center cursor-pointer">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={duplicateCheckQuestions.includes(q.id)}
                                                                    onChange={() => toggleDuplicateCheckQuestion(q.id)}
                                                                    className="w-4 h-4 text-[#AD0101] border-gray-300 rounded"
                                                                />
                                                                <span className="ml-2 text-sm text-gray-700">{q.question}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
                                <button
                                    onClick={handleCancel}
                                    className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                {showDuplicateCheck ? (
                                    view === 'edit' && isFormModified ? (
                                        <button
                                            onClick={handleSaveForm}
                                            disabled={saveLoading}
                                            className="flex items-center gap-2 bg-[#AD0101] text-white px-4 py-2 rounded-lg hover:bg-[#AD0101] transition-colors"
                                            style={{ opacity: saveLoading ? 0.7 : 1, cursor: saveLoading ? 'not-allowed' : 'pointer' }}
                                        >
                                            {saveLoading ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : 'Update Form'}
                                        </button>
                                    ) : (
                                        <button
                                            onClick={handleDuplicateCheckNext}
                                            className="flex items-center gap-2 bg-[#AD0101] text-white px-4 py-2 rounded-lg hover:bg-[#AD0101] transition-colors"
                                        >
                                            Next
                                        </button>
                                    )
                                ) : (
                                    <button
                                        onClick={handleSaveForm}
                                        disabled={saveLoading}
                                        className="flex items-center gap-2 bg-[#AD0101] text-white px-4 py-2 rounded-lg hover:bg-[#AD0101] transition-colors"
                                        style={{ opacity: saveLoading ? 0.7 : 1, cursor: saveLoading ? 'not-allowed' : 'pointer' }}
                                    >
                                        {saveLoading ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : (view === 'edit' ? (isFormModified ? 'Update Form' : 'Next') : 'Save Form')}
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Delete Form Confirmation Modal */}
            <Modal
                open={showDeleteModal}
                footer={null}
                closable={false}
                centered
                width={500}
            >
                <p className="deleteConfirm">Are you sure you want to delete this form? This action cannot be undone.</p>
                <div className="px-4 border-gray-200 flex justify-end gap-3">
                    <button
                        className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                        onClick={cancelDeleteForm}
                        style={{ marginRight: "10px" }}
                    >
                        No
                    </button>

                    <button
                        onClick={confirmDeleteForm}
                        disabled={deleteLoading}
                        className={`flex items-center gap-2 text-white px-4 py-2 rounded-lg transition-colors ${deleteLoading ? 'bg-gray-400' : 'bg-[#AD0101]'}`}
                        style={{ cursor: deleteLoading ? 'not-allowed' : 'pointer' }}
                    >
                        {deleteLoading ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : 'Yes'}
                    </button>
                </div>
            </Modal>

            {/* Delete Question Confirmation Modal */}
            <Modal
                open={showDeleteQuestionModal}
                footer={null}
                closable={false}
                centered
                width={500}
            >
                <p className="deleteConfirm">Are you sure you want to delete this question? This action cannot be undone.</p>
                <div className="px-4 border-gray-200 flex justify-end gap-3">
                    <button
                        className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                        onClick={cancelDeleteQuestion}
                        style={{ marginRight: "10px" }}
                    >
                        No
                    </button>

                    <button
                        onClick={confirmDeleteQuestion}
                        disabled={deleteQuestionLoading}
                        className={`flex items-center gap-2 text-white px-4 py-2 rounded-lg transition-colors ${deleteQuestionLoading ? 'bg-gray-400' : 'bg-[#AD0101]'}`}
                        style={{ cursor: deleteQuestionLoading ? 'not-allowed' : 'pointer' }}
                    >
                        {deleteQuestionLoading ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : 'Yes'}
                    </button>
                </div>
            </Modal>
        </div>
    );
};

export default FormsConfiguration;